import { confirmToast } from "../../utils/toast";
import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosConfig";

const emptyForm = { UserTypeName: "", IsActive: true };
const isAdministratorType = (name) => /(^|\s)admin(?:istrator)?($|\s)/i.test(String(name || "").trim());

const UserTypeMaster = () => {
  const [userTypes, setUserTypes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadUserTypes = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/user-types", { params: { includeInactive: true } });
      setUserTypes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      alert(error.response?.data?.message || error.response?.data || "Unable to load user types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserTypes();
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const userTypeName = form.UserTypeName.trim();
    if (!userTypeName) {
      alert("User Type Name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = { UserTypeName: userTypeName, IsActive: form.IsActive };
      if (editingId) {
        await axios.put(`/user-types/${editingId}`, payload);
      } else {
        await axios.post("/user-types", payload);
      }
      alert(editingId ? "User type updated successfully" : "User type created successfully");
      resetForm();
      await loadUserTypes();
    } catch (error) {
      alert(error.response?.data?.message || error.response?.data || "Unable to save user type");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (userType) => {
    setEditingId(userType.UserTypeID);
    setForm({
      UserTypeName: userType.UserTypeName || "",
      IsActive: Boolean(userType.IsActive)
    });
  };

  const handleDelete = async (userType) => {
    if (!await confirmToast(`Delete user type "${userType.UserTypeName}"?`)) return;
    try {
      await axios.delete(`/user-types/${userType.UserTypeID}`);
      if (editingId === userType.UserTypeID) resetForm();
      await loadUserTypes();
    } catch (error) {
      alert(error.response?.data?.message || error.response?.data || "Unable to delete user type");
    }
  };

  return (
    <div className="container mt-3">
      <h4 className="fw-bold mb-3">User Type Master</h4>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-white fw-semibold">
          {editingId ? "Edit User Type" : "Add User Type"}
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label className="form-label fw-semibold" htmlFor="user-type-name">User Type Name *</label>
                <input
                  id="user-type-name"
                  className="form-control"
                  value={form.UserTypeName}
                  onChange={(event) => setForm((current) => ({ ...current, UserTypeName: event.target.value }))}
                  maxLength={100}
                  required
                />
              </div>
              <div className="col-md-2">
                <div className="form-check mb-2">
                  <input
                    id="user-type-active"
                    type="checkbox"
                    className="form-check-input"
                    checked={form.IsActive}
                    onChange={(event) => setForm((current) => ({ ...current, IsActive: event.target.checked }))}
                    disabled={editingId && isAdministratorType(form.UserTypeName)}
                  />
                  <label className="form-check-label" htmlFor="user-type-active">Active</label>
                </div>
              </div>
              <div className="col-md-4 text-end">
                {editingId && (
                  <button type="button" className="btn btn-secondary me-2" onClick={resetForm} disabled={saving}>Cancel</button>
                )}
                <button type="submit" className="btn btn-success" disabled={saving}>
                  {saving ? "Saving..." : editingId ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-white fw-semibold">User Type List</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 100 }}>ID</th>
                  <th>User Type Name</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th className="text-center" style={{ width: 190 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="4" className="text-center py-4">Loading user types...</td></tr>
                ) : userTypes.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-4 text-muted">No user types found.</td></tr>
                ) : userTypes.map((userType) => {
                  const protectedType = isAdministratorType(userType.UserTypeName);
                  return (
                    <tr key={userType.UserTypeID}>
                      <td>{userType.UserTypeID}</td>
                      <td>{userType.UserTypeName}</td>
                      <td>
                        <span className={`badge ${userType.IsActive ? "bg-success" : "bg-secondary"}`}>
                          {userType.IsActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-center">
                        <button type="button" className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(userType)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(userType)}
                          disabled={protectedType}
                          title={protectedType ? "Administrator user types cannot be deleted" : "Delete user type"}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserTypeMaster;
